import React, { useState, useEffect } from 'react';
import ResponsiveModal from '../../../common/ResponsiveModal';
import toast from 'react-hot-toast';

/**
 * ToolModal Component
 * User-friendly form for creating/editing Vapi tools
 * Based on existing AssistantForm pattern
 */
const ToolModal = ({ isOpen, tool, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'function',
    function_name: '',
    function_description: '',
    parameters: [],
    server_url: '',
    server_timeout_seconds: 20,
    headers: [],
    request_start_blocking: false,  // Default to false like DaveAI
    request_start_content: '',
    is_active: true
  });

  const [jsonError, setJsonError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      if (tool) {
        // Editing existing tool - convert from DB format to form format
        setFormData({
          name: tool.name || '',
          description: tool.description || '',
          type: tool.type || 'function',
          function_name: tool.function_name || '',
          function_description: tool.function_description || '',
          parameters: parseParameters(tool.function_parameters),
          server_url: tool.server_url || '',
          server_timeout_seconds: tool.server_timeout_seconds || 20,
          headers: parseHeaders(tool.server_headers),
          request_start_blocking: parseMessages(tool.messages).blocking,
          request_start_content: parseMessages(tool.messages).content,
          is_active: tool.is_active ?? true
        });
      } else {
        // Creating new tool - reset form
        setFormData({
          name: '',
          description: '',
          type: 'function',
          function_name: '',
          function_description: '',
          parameters: [],
          server_url: '',
          server_timeout_seconds: 20,
          headers: [],
          request_start_blocking: false,
          request_start_content: '',
          is_active: true
        });
      }
      setJsonError(null);
    }
  }, [isOpen, tool]);

  // Convert DB function_parameters to form parameters array
  const parseParameters = (functionParameters) => {
    if (!functionParameters || !functionParameters.properties) return [];
    
    const props = functionParameters.properties;
    const required = functionParameters.required || [];
    
    return Object.keys(props).map(key => ({
      name: key,
      type: props[key].type || 'string',
      description: props[key].description || '',
      required: required.includes(key),
      default: props[key].default || ''
    }));
  };

  // Convert DB server_headers to form headers array
  const parseHeaders = (serverHeaders) => {
    if (!serverHeaders || typeof serverHeaders !== 'object') return [];
    
    return Object.keys(serverHeaders).map(key => ({
      key,
      value: serverHeaders[key]
    }));
  };

  // Extract blocking flag and content from the request-start message
  const parseMessages = (messages) => {
    if (!Array.isArray(messages) || messages.length === 0) {
      return { blocking: false, content: '' };
    }

    const requestStart = messages.find(m => m.type === 'request-start');
    return {
      blocking: requestStart?.blocking || false,
      content: requestStart?.content || ''
    };
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleParameterChange = (index, field, value) => {
    const newParameters = [...formData.parameters];
    newParameters[index] = { ...newParameters[index], [field]: value };
    setFormData(prev => ({ ...prev, parameters: newParameters }));
  };

  const addParameter = () => {
    setFormData(prev => ({
      ...prev,
      parameters: [...prev.parameters, { name: '', type: 'string', description: '', required: false, default: '' }]
    }));
  };

  const removeParameter = (index) => {
    setFormData(prev => ({
      ...prev,
      parameters: prev.parameters.filter((_, i) => i !== index)
    }));
  };

  const handleHeaderChange = (index, field, value) => {
    const newHeaders = [...formData.headers];
    newHeaders[index] = { ...newHeaders[index], [field]: value };
    setFormData(prev => ({ ...prev, headers: newHeaders }));
  };

  const addHeader = () => {
    setFormData(prev => ({
      ...prev,
      headers: [...prev.headers, { key: '', value: '' }]
    }));
  };

  const removeHeader = (index) => {
    setFormData(prev => ({
      ...prev,
      headers: prev.headers.filter((_, i) => i !== index)
    }));
  };

  const buildSubmitData = () => {
    // Convert form data to DB format
    const functionParameters = {
      type: 'object',
      properties: {},
      required: []
    };

    formData.parameters.forEach(param => {
      if (param.name) {
        functionParameters.properties[param.name] = {
          type: param.type,
          description: param.description
        };
        
        if (param.default) {
          functionParameters.properties[param.name].default = param.default;
        }
        
        if (param.required) {
          functionParameters.required.push(param.name);
        }
      }
    });

    const serverHeaders = {};
    formData.headers.forEach(header => {
      if (header.key) {
        serverHeaders[header.key] = header.value;
      }
    });

    const messages = [
      {
        type: 'request-start',
        content: formData.request_start_content.trim(),
        blocking: formData.request_start_blocking || false  // Ensure false, not undefined
      }
    ];

    return {
      name: formData.name.trim(),
      description: formData.description.trim(),
      type: formData.type,
      function_name: formData.function_name.trim(),
      function_description: formData.function_description.trim(),
      function_parameters: functionParameters,
      server_url: formData.server_url.trim(),
      server_timeout_seconds: parseInt(formData.server_timeout_seconds) || 20,
      server_headers: serverHeaders,
      messages,
      is_active: formData.is_active
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      toast.error('Tool name is required');
      return;
    }
    
    if (!formData.function_name.trim()) {
      toast.error('Function name is required');
      return;
    }
    
    if (!formData.function_description.trim()) {
      toast.error('Function description is required');
      return;
    }
    
    if (!formData.server_url.trim()) {
      toast.error('Server URL is required');
      return;
    }

    try {
      const submitData = buildSubmitData();
      onSave(submitData);
    } catch (error) {
      console.error('Error building submit data:', error);
      toast.error(`Invalid form data: ${error.message}`);
    }
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onCancel}
      title={tool ? 'Edit Tool' : 'Create New Tool'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Basic Info Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tool Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="memory_tool"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Brief description of what this tool does"
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => handleChange('is_active', e.target.checked)}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
              Active
            </label>
          </div>
        </div>

        {/* Function Configuration */}
        <div className="space-y-4 border-t pt-4">
          <h3 className="text-lg font-medium text-gray-900">Function Configuration</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Function Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={formData.function_name}
              onChange={(e) => handleChange('function_name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="memory_tool"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Function Description <span className="text-red-500">*</span>
            </label>
            <textarea
              required
              value={formData.function_description}
              onChange={(e) => handleChange('function_description', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Use this tool to retrieve memories. When using this tool to retrieve memories, send the user query."
            />
          </div>

          {/* Parameters Builder */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Parameters
              </label>
              <button
                type="button"
                onClick={addParameter}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                + Add Parameter
              </button>
            </div>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {formData.parameters.map((param, index) => (
                <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 space-y-2">
                    <input
                      type="text"
                      value={param.name}
                      onChange={(e) => handleParameterChange(index, 'name', e.target.value)}
                      placeholder="Parameter name (e.g., query)"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                    />
                    <select
                      value={param.type}
                      onChange={(e) => handleParameterChange(index, 'type', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="string">String</option>
                      <option value="number">Number</option>
                      <option value="boolean">Boolean</option>
                      <option value="object">Object</option>
                      <option value="array">Array</option>
                    </select>
                    <input
                      type="text"
                      value={param.description}
                      onChange={(e) => handleParameterChange(index, 'description', e.target.value)}
                      placeholder="Description"
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                    />
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={param.default}
                        onChange={(e) => handleParameterChange(index, 'default', e.target.value)}
                        placeholder="Default value (optional)"
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500"
                      />
                      <label className="flex items-center text-sm">
                        <input
                          type="checkbox"
                          checked={param.required}
                          onChange={(e) => handleParameterChange(index, 'required', e.target.checked)}
                          className="mr-1"
                        />
                        Required
                      </label>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeParameter(index)}
                    className="text-red-600 hover:text-red-700 p-1"
                  >
                    ✕
                  </button>
                </div>
              ))}
              
              {formData.parameters.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  No parameters defined. Click "Add Parameter" to add one.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Server Configuration */}
        <div className="space-y-4 border-t pt-4">
          <h3 className="text-lg font-medium text-gray-900">Server Configuration</h3>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Server URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              required
              value={formData.server_url}
              onChange={(e) => handleChange('server_url', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="https://automate.fpd35.com/webhook/..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timeout (seconds)
            </label>
            <input
              type="number"
              value={formData.server_timeout_seconds}
              onChange={(e) => handleChange('server_timeout_seconds', e.target.value)}
              min="1"
              max="120"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          {/* Headers Builder */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Headers (Optional)
              </label>
              <button
                type="button"
                onClick={addHeader}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                + Add Header
              </button>
            </div>
            
            <div className="space-y-2">
              {formData.headers.map((header, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={header.key}
                    onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
                    placeholder="Key (e.g., Authorization)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                  <input
                    type="text"
                    value={header.value}
                    onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                    placeholder="Value"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={() => removeHeader(index)}
                    className="text-red-600 hover:text-red-700 px-3"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tool Start Message
            </label>
            <textarea
              value={formData.request_start_content}
              onChange={(e) => handleChange('request_start_content', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Let me look that up for you."
            />
            <p className="mt-1 text-xs text-gray-500">
              Spoken by the assistant as soon as this tool starts running. Leave blank for no message.
            </p>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="request_start_blocking"
              checked={formData.request_start_blocking}
              onChange={(e) => handleChange('request_start_blocking', e.target.checked)}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            />
            <label htmlFor="request_start_blocking" className="ml-2 text-sm text-gray-700">
              Blocking request (wait for tool response before continuing)
            </label>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Saving...' : (tool ? 'Update Tool' : 'Create Tool')}
          </button>
        </div>
      </form>
    </ResponsiveModal>
  );
};

export default ToolModal;